import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  newPlanFeatures,
  oldPlanFeatures,
} from '@app/+authenticated/+account/billing/choose-plan/choose-plan-card/choose-plan-card.model';
import type { SubscriptionResponse } from '@app/+authenticated/+reports/shared/subscriptions/subscription.model';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { Store } from '@ngrx/store';
import { AuthService } from '@reducers/auth/auth.service';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { hasPermission } from '@sb/shared/permission/util';
import { SbDialogService } from '@sb/ui/dialog';
import {
  InvoicePeriodType,
  PlanQuotaModel,
  PlanType,
  QuotaType,
  SubscriptionModel,
} from '@shiftbase-com/ts-shared/models';
import { filter, take } from 'rxjs/operators';

import { SwitchToFreeDialogComponent } from './switch-to-free-dialog/switch-to-free-dialog.component';
import { SwitchToFreeDialogData, SwitchToFreeDialogResult } from './switch-to-free-dialog/switch-to-free-dialog.model';

// Quota types a user can't resolve themselves in the downgrade steps, so they don't force a
// downgrade to Free. SEATS is intentionally NOT here: the downgrade flow's employees step lets the
// user deactivate employees to fit Free's seat limit.
const quotaTypesToFilterOut = [
  QuotaType.ACTIVE_TIME_OFF_BALANCES,
  QuotaType.RATE_CARDS_WITH_MULTIPLE_TIME_BLOCKS,
  QuotaType.RATE_CARDS_WITH_TIME_SURCHARGES,
  QuotaType.ABSENCE_POLICIES,
  QuotaType.HOLIDAY_GROUPS,
];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'trial-expired',
  templateUrl: './trial-expired.component.html',
})
export class TrialExpiredComponent implements OnInit {
  #router = inject(Router);
  #authService = inject(AuthService);
  #store = inject<Store<AppState>>(Store);
  #subscriptionService = inject(SubscriptionService);
  #destroyRef = inject(DestroyRef);
  #featureService = inject(FeatureService);
  #dialogService = inject(SbDialogService);

  mode: 'manager' | 'employee';

  // The account's current plan (the one it was trialing), shown so the manager can continue with it.
  currentPlanName = '';
  currentPlanTagline = '';
  currentPlanFeatures: string[] = [];
  currentPlanFeatureParams: Record<string, unknown> = {};

  // Keep a spinner up until both the plan and the manager/employee mode have resolved, so the page
  // doesn't sit on an empty shell that shows just the heading.
  isLoading = true;
  #subscriptionResolved = false;

  // Free-plan state, resolved on init so "Continue with Free" knows upfront whether the account
  // must downgrade (over the Free quota) or can switch straight away.
  freeOverQuota = false;
  hasFreeOption = false;
  isProcessingFree = false;
  #freeSubscription?: SubscriptionModel;
  #freeQuotaReached: PlanQuotaModel[] = [];

  // Mirror ChoosePlanCardComponent: pick the feature copy that matches the pricing structure on offer.
  #planFeatures = this.#featureService.isEnabled(featureFlag.TMP_ENTITLED_SUBSCRIPTIONS)
    ? newPlanFeatures
    : oldPlanFeatures;

  ngOnInit() {
    this.#loadSubscription();
    this.#store
      .select(getPermissionState)
      .pipe(
        filter((permissionState) => !!permissionState),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((permissionState) => {
        const canManageAccount = hasPermission(
          {
            permissions: ['Manage account'],
            userId: 'me',
            departments: 'any',
          },
          permissionState,
        );

        this.mode = canManageAccount ? 'manager' : 'employee';
        this.#updateLoadingState();
      });

    this.#loadFreePlanState();
  }

  confirmPlan() {
    this.#router.navigate(['', { outlets: { modal: ['subscription'] } }]);
  }

  continueWithFree() {
    if (this.isProcessingFree) {
      return;
    }

    if (this.freeOverQuota) {
      this.#openSwitchToFreeDialog();
      return;
    }

    this.#confirmFreeSubscription();
  }

  #openSwitchToFreeDialog() {
    this.#dialogService
      .setSize('sm')
      .open<SwitchToFreeDialogComponent, SwitchToFreeDialogData, SwitchToFreeDialogResult>(
        SwitchToFreeDialogComponent,
        {
          data: { quotaReached: this.#freeQuotaReached },
        },
      )
      .closed.pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result === 'keep-plan') {
          this.confirmPlan();
        } else if (result === 'switch-to-free') {
          this.#goToDowngradeSteps();
        }
      });
  }

  // Only reached when the account already fits the Free quota, so switching to Free succeeds without
  // any adjustment — save the plan directly (no user input, no billing) and drop them into the product.
  #confirmFreeSubscription() {
    if (!this.#freeSubscription) {
      return;
    }

    this.isProcessingFree = true;

    this.#subscriptionService
      .saveSubscription({
        subscriptionId: this.#freeSubscription.id,
        integrationPlus: false,
        invoicePeriod: InvoicePeriodType.MONTH,
        compliance: false,
        hrPlus: false,
        chat: false,
      })
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => this.#landInProduct(),
        error: () => (this.isProcessingFree = false),
      });
  }

  // Over the Free quota: the PUT to /subscription is rejected while the account exceeds Free's limits
  // ("Your account exceeds the usage quotas of the desired plan"), so the user must first remove the
  // excess teams/departments in the downgrade steps, which then switches the account to Free.
  #goToDowngradeSteps() {
    this.#router.navigate(['/downgrade'], { queryParams: { plan: PlanType.FREE } }).catch(() => undefined);
  }

  #landInProduct() {
    const intendedRoute = this.#authService.redirectUrl() || '/';

    this.#router
      .navigate([intendedRoute])
      .then(() => window.location.reload())
      .catch(() => undefined);
  }

  // Fetching the subscription also populates the store, which the plan/subscription modal reads once
  // the manager confirms a plan. The response additionally drives the current plan card.
  #loadSubscription() {
    this.#subscriptionService
      .getSubscription()
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (subscription: SubscriptionResponse) => {
          this.#setCurrentPlan(subscription);
          this.#markSubscriptionResolved();
        },
        // Stop the spinner even if the request fails, so the page doesn't spin forever.
        error: () => this.#markSubscriptionResolved(),
      });
  }

  #markSubscriptionResolved() {
    this.#subscriptionResolved = true;
    this.#updateLoadingState();
  }

  #updateLoadingState() {
    this.isLoading = !(this.mode && this.#subscriptionResolved);
  }

  // Once the trial ends the account is converted to Free, so its live subscription is Free. The plan
  // the manager was actually trialing (the one to continue with) comes back as previousSubscription;
  // fall back to the live subscription for accounts that were never converted.
  #setCurrentPlan(subscription: SubscriptionResponse) {
    const plan = subscription.previousSubscription?.plan ?? subscription.plan;

    // Always surface the plan name so the card and its CTAs render even when the plan has no feature
    // copy configured; the tagline and feature list are optional and stay empty in that case.
    this.currentPlanName = plan.name;

    const planFeatureConfig = this.#planFeatures[plan.weight];

    if (!planFeatureConfig) {
      return;
    }

    const seatsQuota = plan.quotas.find((quota) => quota.type === QuotaType.SEATS);

    this.currentPlanTagline = planFeatureConfig.tagline ?? '';
    this.currentPlanFeatures = planFeatureConfig.features ?? [];
    this.currentPlanFeatureParams = {
      seatsLimit: seatsQuota?.limit ?? '',
    };
  }

  #loadFreePlanState() {
    this.#subscriptionService
      .getSubscriptions()
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe((subscriptions) => this.#setFreePlanState(subscriptions));
  }

  #setFreePlanState(subscriptions: SubscriptionModel[]) {
    const freeSubscription = subscriptions.find((subscription) => subscription.plan.type === PlanType.FREE);

    if (!freeSubscription) {
      return;
    }

    this.#freeSubscription = freeSubscription;
    this.hasFreeOption = true;
    this.#freeQuotaReached = this.#getDowngradeQuotas(freeSubscription);
    this.freeOverQuota = this.#freeQuotaReached.length > 0;
  }

  #getDowngradeQuotas(subscription: SubscriptionModel): PlanQuotaModel[] {
    return Object.values(subscription.plan.quotas).filter(
      (quota) => quota.usage > quota.limit && !quotaTypesToFilterOut.includes(quota.type),
    );
  }

  backToLogin() {
    this.#authService.logout().then(() => this.#router.navigate(['login']));
  }
}
